import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  FileText, ArrowLeft, Download, Link2, GitBranch, ListChecks, Trash2, Calendar, ArrowDownToLine, X, Sparkles, Loader2, ShieldCheck, CheckCircle2, PenLine, Clock,
} from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { STATO_BADGE } from "@/lib/constants";
import { Markdown } from "@/components/Markdown";
import { toast } from "sonner";

function Field({ label, children }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className="mt-1 text-sm">{children || <span className="text-slate-500">—</span>}</div>
    </div>
  );
}

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isConsultant = user?.role === "consultant";
  const [doc, setDoc] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [impact, setImpact] = useState(null);
  const [allDocs, setAllDocs] = useState([]);
  const [summary, setSummary] = useState("");
  const [sumLoading, setSumLoading] = useState(false);
  const [coherence, setCoherence] = useState("");
  const [cohLoading, setCohLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [responsabile, setResponsabile] = useState("");
  const [apprLoading, setApprLoading] = useState(false);
  useEffect(() => {
    if (doc?.summary) setSummary(doc.summary);
    if (doc?.coherence) setCoherence(doc.coherence);
  }, [doc]);
  const genSummary = async () => {
    setSumLoading(true);
    try {
      const r = await api.post(`/documents/${id}/summary${summary ? "?refresh=true" : ""}`);
      setSummary(r.data.summary);
    } catch (e) { toast.error("Sintesi non riuscita"); }
    finally { setSumLoading(false); }
  };
  const runCoherence = async () => {
    setCohLoading(true);
    try {
      const r = await api.post(`/documents/${id}/coherence${coherence ? "?refresh=true" : ""}`);
      setCoherence(r.data.result);
    } catch (e) { toast.error("Verifica coerenza non riuscita"); }
    finally { setCohLoading(false); }
  };
  const exportCoherencePdf = async () => {
    try {
      const r = await api.post(`/documents/${id}/coherence/pdf`, { result: coherence }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `verifica_coerenza_${doc?.codice || id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Esportazione PDF non riuscita"); }
  };

  useEffect(() => {
    if (isConsultant && doc?.tenant_id) {
      api.get(`/documents?tenant_id=${doc.tenant_id}`).then((r) => setAllDocs(r.data)).catch(() => {});
      api.get(`/documents/${id}/link-suggestions`).then((r) => setSuggestions(r.data.suggestions)).catch(() => {});
    }
  }, [doc, isConsultant]);

  const saveLinks = async (ids) => {
    await api.put(`/documents/${id}/links`, { linked_document_ids: ids });
    const r = await api.get(`/documents/${id}`); setDoc(r.data);
    api.get(`/documents/${id}/impact`).then((x) => setImpact(x.data)).catch(() => {});
    api.get(`/documents/${id}/link-suggestions`).then((x) => setSuggestions(x.data.suggestions)).catch(() => {});
    toast.success("Collegamenti aggiornati");
  };

  useEffect(() => {
    api.get(`/documents/${id}`).then((r) => setDoc(r.data)).catch(() => navigate("/documenti"));
    api.get(`/documents/${id}/impact`).then((r) => setImpact(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { if (doc) setResponsabile(doc.responsabile || ""); }, [doc]);

  const setApproval = async (newState) => {
    setApprLoading(true);
    try {
      const r = await api.post(`/documents/${id}/approval`, { stato_approvazione: newState, responsabile });
      setDoc(r.data);
      toast.success("Stato di approvazione aggiornato");
    } catch (e) { toast.error("Aggiornamento non riuscito"); }
    finally { setApprLoading(false); }
  };

  const [availReqs, setAvailReqs] = useState([]);
  useEffect(() => {
    if (isConsultant && doc?.id) {
      api.get(`/documents/${id}/available-requirements`).then((r) => {
        const flat = [];
        (r.data.schemes || []).forEach((g) => g.requirements.forEach((rq) =>
          flat.push({ ...rq, sigla: g.sigla, scheme_id: g.scheme_id })));
        setAvailReqs(flat);
      }).catch(() => setAvailReqs([]));
    }
  }, [doc, isConsultant]);

  useEffect(() => {
    if (!doc) return;
    const covered = (doc.requirement_ids || []);
    setReqs(availReqs.filter((x) => covered.includes(x.id)));
  }, [doc, availReqs]);

  const saveRequirements = async (ids) => {
    try {
      await api.put(`/documents/${id}/requirements`, { requirement_ids: ids });
      const r = await api.get(`/documents/${id}`); setDoc(r.data);
      toast.success("Requisiti coperti aggiornati");
    } catch (e) { toast.error("Aggiornamento requisiti non riuscito"); }
  };
  const addRequirement = (rid) => {
    const ids = Array.from(new Set([...(doc.requirement_ids || []), rid]));
    saveRequirements(ids);
  };
  const removeRequirement = (rid) => {
    saveRequirements((doc.requirement_ids || []).filter((x) => x !== rid));
  };

  const remove = async () => {
    if (!window.confirm("Eliminare questo documento?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Documento eliminato");
    navigate("/documenti");
  };

  if (!doc) return null;

  const STEPS = [
    { key: "bozza", label: "Bozza", icon: PenLine },
    { key: "in_verifica", label: "In verifica", icon: Clock },
    { key: "approvato", label: "Approvato", icon: CheckCircle2 },
  ];
  const curIdx = Math.max(0, STEPS.findIndex((s) => s.key === (doc.stato_approvazione || "bozza")));

  return (
    <div className="space-y-6 max-w-5xl">
      <Link to="/documenti" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4 mr-1" /> Torna ai documenti
      </Link>

      <div className="flex items-start justify-between">
        <div className="flex gap-4">
          <div className="h-12 w-12 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
            <FileText className="h-6 w-6 text-blue-700" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-slate-500">{doc.codice}</span>
              <Badge variant="outline">{doc.tipo}</Badge>
              <Badge className={STATO_BADGE[doc.stato] || ""}>{doc.stato}</Badge>
            </div>
            <h1 className="font-display text-2xl font-semibold tracking-tight mt-1">{doc.titolo}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          {doc.file?.storage_path && (
            <a href={fileUrl(doc.file.storage_path)} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="download-file-btn"><Download className="h-4 w-4 mr-2" />Allegato</Button>
            </a>
          )}
          {isConsultant && (
            <>
              <Button variant="outline" onClick={() => navigate(`/documenti/nuovo?edit=${doc.id}`)}>Modifica</Button>
              <Button variant="ghost" className="text-red-600" onClick={remove} data-testid="delete-doc-btn"><Trash2 className="h-4 w-4" /></Button>
            </>
          )}
        </div>
      </div>

      {isConsultant && (
        <Card className="p-6 border-slate-200" data-testid="approval-card">
          <div className="flex items-center gap-2 mb-5"><PenLine className="h-4 w-4 text-indigo-600" /><h2 className="font-display font-medium">Flusso di approvazione</h2></div>
          <div className="flex items-center">
            {STEPS.map((s, i) => {
              const active = i <= curIdx;
              const current = i === curIdx;
              return (
                <div key={s.key} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center">
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center border transition-all duration-300 ${active ? "bg-gradient-to-br from-blue-600 to-indigo-500 text-white border-transparent shadow-md shadow-indigo-500/30" : "bg-white text-slate-400 border-slate-200"} ${current ? "ring-4 ring-indigo-200" : ""}`}>
                      <s.icon className="h-4 w-4" />
                    </div>
                    <span className={`mt-2 text-xs font-medium ${active ? "text-slate-800" : "text-slate-400"}`}>{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-2 rounded transition-colors duration-300 ${i < curIdx ? "bg-indigo-400" : "bg-slate-200"}`} />}
                </div>
              );
            })}
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 md:items-end">
            <div>
              <label className="label-caps">Responsabile</label>
              <Input className="mt-1" placeholder="Nome del responsabile" value={responsabile} onChange={(e) => setResponsabile(e.target.value)} data-testid="approval-responsabile" />
            </div>
            <div className="flex gap-2">
              {curIdx === 0 && <Button onClick={() => setApproval("in_verifica")} disabled={apprLoading} data-testid="approval-submit"><Clock className="h-4 w-4 mr-2" />Invia in verifica</Button>}
              {curIdx === 1 && <>
                <Button variant="outline" onClick={() => setApproval("bozza")} disabled={apprLoading} data-testid="approval-back">Rimanda in bozza</Button>
                <Button onClick={() => setApproval("approvato")} disabled={apprLoading} data-testid="approval-approve"><CheckCircle2 className="h-4 w-4 mr-2" />Approva</Button>
              </>}
              {curIdx === 2 && <Button variant="outline" onClick={() => setApproval("bozza")} disabled={apprLoading} data-testid="approval-reopen"><PenLine className="h-4 w-4 mr-2" />Riapri (bozza)</Button>}
            </div>
          </div>
          {doc.approvato_da && curIdx === 2 && (
            <p className="mt-4 text-xs text-emerald-700 flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="approval-stamp">
              <CheckCircle2 className="h-3.5 w-3.5" />Approvato da <strong>{doc.approvato_da}</strong> il {new Date(doc.approvato_il).toLocaleDateString("it-IT")}
            </p>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-slate-200">
            <Field label="Descrizione">
              <p className="whitespace-pre-wrap leading-relaxed">{doc.descrizione}</p>
            </Field>
          </Card>

          <Card className="p-6 border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-700" /><h2 className="font-display font-medium">Sintesi AI di Steve</h2></div>
              {isConsultant && (
                <Button size="sm" variant="outline" onClick={genSummary} disabled={sumLoading} data-testid="gen-summary-btn">
                  {sumLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (summary ? "Rigenera" : "Genera sintesi")}
                </Button>
              )}
            </div>
            {summary
              ? <div data-testid="doc-summary"><Markdown>{summary}</Markdown></div>
              : <p className="text-sm text-slate-500">{isConsultant ? "Chiedi a Steve una sintesi del documento in fase di revisione." : "Nessuna sintesi disponibile per questo documento."}</p>}
          </Card>

          {isConsultant && (
            <Card className="p-6 border-slate-200" data-testid="coherence-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-700" /><h2 className="font-display font-medium">Verifica coerenza ISO</h2></div>
                <div className="flex gap-2">
                  {coherence && (
                    <Button size="sm" variant="outline" onClick={exportCoherencePdf} data-testid="export-coherence-pdf-btn">
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={runCoherence} disabled={cohLoading} data-testid="run-coherence-btn">
                    {cohLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (coherence ? "Rivaluta" : "Verifica coerenza")}
                  </Button>
                </div>
              </div>
              {coherence
                ? <div data-testid="coherence-result"><Markdown>{coherence}</Markdown></div>
                : <p className="text-sm text-slate-500">Confronta il documento con le fonti normative (ISO 9001, 14001, 45001, SA8000, UNI 16101, 17021-3, PdR) ed evidenzia requisiti coperti e criticità.</p>}
            </Card>
          )}

          <Card className="p-6 border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <Link2 className="h-4 w-4 text-blue-700" />
              <h2 className="font-display font-medium">Documenti collegati</h2>
            </div>
            {doc.linked_documents?.length ? (
              <div className="space-y-2" data-testid="linked-documents">
                {doc.linked_documents.map((l) => (
                  <div key={l.id} className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors">
                    <Link to={`/documenti/${l.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="font-mono text-sm text-slate-500">{l.codice}</span>
                      <span className="text-sm truncate">{l.titolo}</span>
                    </Link>
                    {isConsultant && (
                      <button onClick={() => saveLinks(doc.linked_documents.filter((x) => x.id !== l.id).map((x) => x.id))}
                        className="text-slate-500 hover:text-red-600 shrink-0" data-testid={`unlink-${l.id}`}><X className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">Nessun collegamento.</p>}
            {isConsultant && (
              <div className="mt-4" data-testid="add-link-row">
                <Select value="" onValueChange={(v) => saveLinks([...(doc.linked_documents || []).map((x) => x.id), v])}>
                  <SelectTrigger className="h-9" data-testid="add-link-select"><SelectValue placeholder="+ Aggiungi collegamento..." /></SelectTrigger>
                  <SelectContent>
                    {allDocs.filter((d2) => d2.id !== doc.id && !(doc.linked_documents || []).some((x) => x.id === d2.id)).map((d2) => (
                      <SelectItem key={d2.id} value={d2.id}>{d2.codice ? d2.codice + " — " : ""}{d2.titolo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isConsultant && suggestions.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100" data-testid="link-suggestions">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
                  <span className="label-caps">Collegamenti suggeriti dall'AI</span>
                </div>
                <div className="space-y-2">
                  {suggestions.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-md border border-dashed border-slate-200 hover:border-indigo-400 transition-colors" data-testid={`suggestion-${s.id}`}>
                      <span className="font-mono text-xs text-slate-500">{s.codice}</span>
                      <span className="text-sm truncate flex-1">{s.titolo}</span>
                      <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-200">{s.percent}% affine</Badge>
                      <Button size="sm" variant="ghost" className="h-7 text-blue-700 shrink-0"
                        onClick={() => saveLinks([...(doc.linked_documents || []).map((x) => x.id), s.id])}
                        data-testid={`add-suggestion-${s.id}`}>Aggiungi</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <Card className="p-6 border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownToLine className="h-4 w-4 text-blue-700" />
              <h2 className="font-display font-medium">Documenti che dipendono da questo</h2>
            </div>
            <p className="text-xs text-slate-500 mb-4">Da rivedere in caso di modifica (analisi d'impatto).</p>
            {impact?.incoming?.length ? (
              <div className="space-y-2" data-testid="impact-incoming">
                {impact.incoming.map((l) => (
                  <Link key={l.id} to={`/documenti/${l.id}`}
                    className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors">
                    <span className="font-mono text-sm text-slate-500">{l.codice}</span>
                    <span className="text-sm flex-1">{l.titolo}</span>
                    <Badge variant="outline">{l.tipo}</Badge>
                  </Link>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">Nessun documento dipende da questo.</p>}
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6 border-slate-200 space-y-4">
            <Field label="Versione">{doc.versione}</Field>
            <Field label="Data emissione"><span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-slate-500" />{doc.data_emissione}</span></Field>
            <Field label="Prossima revisione"><span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-slate-500" />{doc.data_prossima_revisione}</span></Field>
          </Card>

          <Card className="p-6 border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <GitBranch className="h-4 w-4 text-blue-700" />
              <h3 className="text-sm font-medium">Processi</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {doc.processes?.length ? doc.processes.map((p) => <Badge key={p.id} variant="outline">{p.nome}</Badge>) : <span className="text-sm text-slate-500">—</span>}
            </div>
          </Card>

          {isConsultant && (
            <Card className="p-6 border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <ListChecks className="h-4 w-4 text-blue-700" />
                <h3 className="text-sm font-medium">Requisiti coperti</h3>
              </div>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="covered-requirements">
                {reqs.length ? reqs.map((r) => (
                  <Badge key={r.id} variant="outline" className="font-mono gap-1 pr-1" data-testid={`req-badge-${r.id}`}>
                    {r.sigla ? <span className="text-[10px] text-slate-400 font-sans">{r.sigla}</span> : null}
                    {r.codice}
                    <button onClick={() => removeRequirement(r.id)} data-testid={`req-remove-${r.id}`}
                      className="ml-0.5 rounded hover:bg-red-100 hover:text-red-600 p-0.5" title="Rimuovi copertura">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )) : <span className="text-sm text-slate-500">Nessun requisito collegato.</span>}
              </div>
              <Select value="" onValueChange={addRequirement}>
                <SelectTrigger className="w-full" data-testid="req-add-select">
                  <SelectValue placeholder="+ Aggiungi manualmente un requisito coperto" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {availReqs.filter((r) => !(doc.requirement_ids || []).includes(r.id)).length === 0 ? (
                    <div className="px-3 py-2 text-sm text-slate-400">Tutti i requisiti disponibili sono già collegati.</div>
                  ) : availReqs.filter((r) => !(doc.requirement_ids || []).includes(r.id)).map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`req-option-${r.id}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className="shrink-0 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">{r.sigla}</span>
                        <span className="font-mono text-xs">{r.codice}</span>
                        <span className="text-slate-600">{r.titolo}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-2">Puoi aggiungere o rimuovere manualmente le coperture se l'AI ne ha mancata qualcuna.</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
